import type { OutcomeReason, PortalStatus, PositionStage, SubmissionStatus, WaitingOn } from './enums';
import { addMonths, compareIso, diffDays, type IsoDate } from './dates';

/**
 * Workflow rules (spec §6). Pure functions; the API services apply the side effects.
 */

export const TERMINAL_SUBMISSION_STATUSES: readonly SubmissionStatus[] = ['PLACED', 'REJECTED', 'WITHDRAWN'];
export const CLOSED_POSITION_STAGES: readonly PositionStage[] = ['FILLED', 'LOST', 'DROPPED'];
export const IN_PROGRESS_SUBMISSION_STATUSES: readonly SubmissionStatus[] = [
  'SCREENING',
  'SUBMITTED',
  'HR_INTERVIEW',
  'CLIENT_INTERVIEW',
  'OFFER',
];

const STATUS_RANK: Record<SubmissionStatus, number> = {
  SCREENING: 0,
  SUBMITTED: 1,
  HR_INTERVIEW: 2,
  CLIENT_INTERVIEW: 3,
  OFFER: 4,
  PLACED: 5,
  REJECTED: 5,
  WITHDRAWN: 5,
};

const STAGE_RANK: Record<PositionStage, number> = {
  NEW: 0,
  SOURCING: 1,
  SUBMITTED: 2,
  INTERVIEWING: 3,
  OFFER: 4,
  FILLED: 5,
  LOST: 5,
  DROPPED: 5,
};

export function isTerminalStatus(status: SubmissionStatus): boolean {
  return TERMINAL_SUBMISSION_STATUSES.includes(status);
}

export function isClosedStage(stage: PositionStage): boolean {
  return CLOSED_POSITION_STAGES.includes(stage);
}

/** Submitted or later, i.e. the candidate has left Screening (in progress or finished). */
export function isPastScreening(status: SubmissionStatus): boolean {
  return status !== 'SCREENING';
}

export interface CountsAsSubmittedInput {
  status: SubmissionStatus;
  sentOn?: IsoDate | Date | null;
}

/**
 * "Submitted or later" for the position's submitted_count: in-progress statuses past Screening,
 * Placed, and Rejected/Withdrawn only when the CV had actually been sent (sent_on set).
 */
export function countsAsSubmitted(s: CountsAsSubmittedInput): boolean {
  if (s.status === 'PLACED') return true;
  if (isTerminalStatus(s.status)) return s.sentOn != null;
  return STATUS_RANK[s.status] >= STATUS_RANK.SUBMITTED;
}

export function stageRank(stage: PositionStage): number {
  return STAGE_RANK[stage];
}

export function statusRank(status: SubmissionStatus): number {
  return STATUS_RANK[status];
}

/** Outcome reason is required when the status is Rejected, Withdrawn or Placed. */
export function outcomeReasonRequired(status: SubmissionStatus): boolean {
  return isTerminalStatus(status);
}

/** The position stage a submission status pulls the position to (never backwards, spec §6.2). */
export function stageForSubmissionStatus(status: SubmissionStatus): PositionStage | null {
  switch (status) {
    case 'SUBMITTED':
      return 'SUBMITTED';
    case 'HR_INTERVIEW':
    case 'CLIENT_INTERVIEW':
      return 'INTERVIEWING';
    case 'OFFER':
      return 'OFFER';
    case 'PLACED':
      return 'FILLED';
    default:
      return null;
  }
}

/**
 * Returns the new stage if the target is later than the current one, else null (stage never moves
 * backwards automatically). A closed position only ever moves to Filled (a placement wins over Lost/Dropped).
 */
export function advanceStage(current: PositionStage, target: PositionStage | null): PositionStage | null {
  if (!target) return null;
  if (isClosedStage(current)) return target === 'FILLED' && current !== 'FILLED' ? 'FILLED' : null;
  return STAGE_RANK[target] > STAGE_RANK[current] ? target : null;
}

export interface NeedsChaseInput {
  status: SubmissionStatus;
  followUpOn?: IsoDate | null;
  feedbackDue?: IsoDate | null;
  waitingOn?: WaitingOn | null;
}

/**
 * needs_chase = status not terminal AND (follow_up_on ≤ today OR (feedback_due < today AND waiting_on in Client/Intermediary)).
 */
export function needsChase(s: NeedsChaseInput, today: IsoDate): boolean {
  if (isTerminalStatus(s.status)) return false;
  if (s.followUpOn && compareIso(s.followUpOn, today) <= 0) return true;
  if (
    s.feedbackDue &&
    compareIso(s.feedbackDue, today) < 0 &&
    (s.waitingOn === 'CLIENT' || s.waitingOn === 'INTERMEDIARY')
  ) {
    return true;
  }
  return false;
}

export interface IsOpenInput {
  portalStatus: PortalStatus;
  stage: PositionStage;
}

/** is_open = portal Open AND stage not Filled/Lost/Dropped. */
export function isPositionOpen(p: IsOpenInput): boolean {
  return p.portalStatus === 'OPEN' && !isClosedStage(p.stage);
}

export interface IsUrgentInput {
  startDate?: IsoDate | null;
  stage: PositionStage;
  submissionStatuses: readonly SubmissionStatus[];
}

/** is_urgent = start within urgent_start_days AND no submission past Screening (in progress). */
export function isPositionUrgent(p: IsUrgentInput, today: IsoDate, urgentStartDays: number): boolean {
  if (!p.startDate || isClosedStage(p.stage)) return false;
  const days = diffDays(today, p.startDate);
  if (days > urgentStartDays) return false;
  const hasProgress = p.submissionStatuses.some((s) => isPastScreening(s) && !isTerminalStatus(s));
  return !hasProgress;
}

export function consentExpiry(
  consentDate: IsoDate | null | undefined,
  retentionMonths: number,
): IsoDate | null {
  if (!consentDate) return null;
  return addMonths(consentDate, retentionMonths);
}

export function consentExpiresWithin(
  consentDate: IsoDate | null | undefined,
  retentionMonths: number,
  today: IsoDate,
  withinDays: number,
): boolean {
  const expiry = consentExpiry(consentDate, retentionMonths);
  if (!expiry) return false;
  const d = diffDays(today, expiry);
  return d <= withinDays;
}

export interface ClosingDecision {
  submissionId: string;
  status: SubmissionStatus;
  /** Proposed new status (Screening → Rejected). Null when the user must decide. */
  proposedStatus: SubmissionStatus | null;
  proposedReason: OutcomeReason | null;
  requiresDecision: boolean;
  note: string;
}

/**
 * Lost/Dropped dialog (spec §6.1): Screening submissions are proposed as Rejected with reason
 * "Client closed the role"; submissions past Submitted are never auto-rejected.
 */
export function proposeClosingDecisions(
  submissions: ReadonlyArray<{ id: string; status: SubmissionStatus }>,
): ClosingDecision[] {
  return submissions
    .filter((s) => !isTerminalStatus(s.status))
    .map((s) =>
      s.status === 'SCREENING'
        ? {
            submissionId: s.id,
            status: s.status,
            proposedStatus: 'REJECTED' as SubmissionStatus,
            proposedReason: 'CLIENT_CLOSED_ROLE' as OutcomeReason,
            requiresDecision: false,
            note: 'Screening → proposed Rejected (Client closed the role)',
          }
        : {
            submissionId: s.id,
            status: s.status,
            proposedStatus: null,
            proposedReason: null,
            requiresDecision: true,
            note: 'Already in the client process; decide individually (can continue after the request closed for new CVs)',
          },
    );
}

/**
 * After a rejection/withdrawal: if the position has no submission left in progress and is at
 * Submitted or Interviewing, suggest moving it back to Sourcing.
 */
export function suggestBackToSourcing(
  stage: PositionStage,
  remainingStatuses: readonly SubmissionStatus[],
): boolean {
  if (stage !== 'SUBMITTED' && stage !== 'INTERVIEWING') return false;
  return !remainingStatuses.some((s) => !isTerminalStatus(s));
}

/** The on-site rule (spec §6.1): regular on-site presence is not taken on. */
export function isOnSiteBlocked(p: { onSiteFlag?: boolean | null; workModel?: string | null }): boolean {
  return Boolean(p.onSiteFlag) || p.workModel === 'ON_SITE';
}

/** Heuristic used by the portal upsert to flag regular on-site presence in free text. */
export function detectRegularOnSite(text: string | null | undefined): boolean {
  if (!text) return false;
  const t = text.toLowerCase();
  const patterns: RegExp[] = [
    /\b\d+\s*(days?|tage?)\s*(per|a|pro|\/)\s*(week|woche)/,
    /\b(\d+|several|mehrere|zwei|drei|two|three)\s*(days?|tage?)\s*(per|a|pro|im|\/)\s*(month|monat)/,
    /\bon[- ]?site onboarding\b/,
    /\bonboarding (vor ort|on[- ]?site)\b/,
    /\b100\s*%\s*(on[- ]?site|vor ort)\b/,
    /\b(fully|full[- ]time|ausschliesslich|ausschließlich|nur)\s*(on[- ]?site|vor ort)\b/,
    /\bon[- ]?site\s*(only|required|mandatory|pflicht)\b/,
    /\b(präsenz|praesenz)pflicht\b/,
    /\bhybrid(?:[^.]{0,40})\b(\d+|zwei|drei|two|three)\s*(tage?|days?)\b/,
  ];
  return patterns.some((re) => re.test(t));
}
